import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import useSEO from "@/lib/useSEO";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { SECTORS, ORG_TYPES } from "@/data/site";

const Industries = () => {
  useSEO({
    title: "Industries",
    description:
      "Arun Gupta & Co. has professional experience across manufacturing, trading, real estate, logistics, steel and metals and other commercial businesses in and around Kanpur.",
    path: "/industries",
  });

  return (
    <>
      <PageHero
        eyebrow="Industries"
        titleLines={["Experience across", "businesses. Understanding", "beyond the numbers."]}
        intro="We work with businesses that differ widely in how they operate. What connects them is a need for financial information they can rely on and advice grounded in their reality."
        testId="industries-hero"
      />

      {/* Intro */}
      <section className="py-14 lg:py-20" data-testid="industries-intro">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="font-serif text-navy text-3xl lg:text-4xl tracking-tight leading-[1.14]">
                Breadth of experience, not narrow specialisation.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.1}>
              <p className="text-lg font-light leading-relaxed text-charcoal/85">
                Rather than claiming deep specialism in every field, we bring practical familiarity
                with a range of operating models and commercial realities. That breadth helps us ask
                better questions and interpret a set of numbers in the context that produced them.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Sectors list */}
      <section className="py-16 lg:py-24 border-t border-border-subtle" data-testid="industries-sectors">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <div className="overline">Sectors we have worked with</div>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2">
            {SECTORS.map((s, i) => (
              <Reveal key={s} delay={(i % 2) * 0.05}>
                <div className="group flex items-baseline gap-6 py-6 border-b border-border-subtle">
                  <span className="text-brass text-xs font-semibold tracking-widest w-8">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-serif text-navy text-2xl lg:text-3xl group-hover:text-brass transition-colors duration-500">{s}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Organisation types */}
      <section className="py-14 lg:py-20" style={{ background: "#EFEBE1" }} data-testid="industries-orgs">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4">
            <Reveal>
              <div className="overline">Organisations we serve</div>
              <h2 className="font-serif text-navy text-3xl lg:text-4xl tracking-tight mt-5 leading-[1.14]">
                From owner-managed firms to growing companies.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7 lg:col-start-6">
            {ORG_TYPES.map((o, i) => (
              <Reveal key={o} delay={i * 0.06}>
                <div className="py-5 border-b border-navy/15">
                  <span className="font-serif text-navy text-xl lg:text-2xl">{o}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Confidentiality */}
      <section className="py-14 lg:py-20 border-t border-border-subtle" data-testid="industries-confidentiality">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <div className="max-w-3xl">
              <span className="font-serif text-brass text-5xl leading-none">“</span>
              <p className="font-serif text-navy text-2xl lg:text-[2rem] leading-[1.35] mt-2">
                Many of our client relationships have developed over years of professional
                association. In keeping with the importance we place on confidentiality and
                professional discretion, we generally do not publicly identify our clients or
                disclose details of their engagements.”
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24 border-t border-border-subtle" style={{ background: "#17263D" }} data-testid="industries-cta">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 text-center">
          <Reveal>
            <h2 className="font-serif text-ivory text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1] max-w-2xl mx-auto">
              Wondering how our experience applies to your business?
            </h2>
            <div className="mt-10 flex justify-center">
              <Link to="/contact" className="btn-solid" style={{ background: "#B39A62", borderColor: "#B39A62", color: "#17263D" }} data-testid="industries-cta-btn">
                Start a Conversation
                <ArrowRight size={15} strokeWidth={1.5} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Industries;
